/**
 * SuggestInput — a text input that offers matches as you type.
 *
 * Free typing always wins: the suggestions are an offer, never a gate, so a
 * value that matches nothing is simply kept (the Quick Bill form saves it for
 * next time). ↑/↓ move, Enter picks the highlighted match, Esc closes.
 * A match is highlighted up front only when it is exactly what was typed, so
 * Enter never swaps typed text for a near match; with nothing highlighted,
 * Enter is passed through to the caller.
 */
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '@/utils/cn';

export interface SuggestView {
  primary: string;
  secondary?: string;
  tag?: string;
}

interface Props<T> {
  value: string;
  onChange: (value: string) => void;
  fetch: (query: string) => Promise<T[]>;
  view: (item: T) => SuggestView;
  onPick: (item: T) => void;
  /** A last, non-selectable line — e.g. "New buyer: saved with the bill". */
  hint?: (query: string, results: T[]) => ReactNode | null;
  /** Is this suggestion exactly the typed text? Default: its primary line, ignoring case. */
  isExact?: (item: T, query: string) => boolean;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
  listClassName?: string;
  inputMode?: 'text' | 'numeric' | 'decimal';
  autoFocus?: boolean;
  testId?: string;
}

export function SuggestInput<T>({
  value, onChange, fetch, view, onPick, hint, isExact, onKeyDown,
  placeholder, ariaLabel, className, listClassName, inputMode, autoFocus, testId,
}: Props<T>) {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<T[]>([]);
  const [hl, setHl] = useState(-1);
  // Only look things up after the user types — not when a pick fills the field.
  const typed = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Read at lookup time; callers pass fresh closures every render.
  const viewRef = useRef(view);
  viewRef.current = view;
  const isExactRef = useRef(isExact);
  isExactRef.current = isExact;

  // Placed against the viewport, not the parent: the form scrolls, and an
  // absolutely positioned list is clipped by any scrolling ancestor. Opens
  // upwards when there is not room below.
  const [pos, setPos] = useState<CSSProperties>({});
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = inputRef.current?.getBoundingClientRect();
      if (!r) return;
      const below = window.innerHeight - r.bottom;
      const up = below < 260 && r.top > below;
      setPos({
        position: 'fixed',
        left: r.left,
        minWidth: r.width,
        ...(up ? { bottom: window.innerHeight - r.top + 4, maxHeight: Math.min(288, r.top - 12) }
               : { top: r.bottom + 4, maxHeight: Math.min(288, below - 12) }),
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open]);

  useEffect(() => {
    if (!typed.current) return;
    const q = value.trim();
    if (!q) { setResults([]); setOpen(false); return; }
    let alive = true;
    const t = setTimeout(() => {
      fetch(q)
        .then((r) => {
          if (!alive) return;
          const exact = isExactRef.current ?? ((item: T, query: string) => viewRef.current(item).primary.toLowerCase() === query.toLowerCase());
          setResults(r);
          setHl(r.findIndex((item) => exact(item, q)));
          setOpen(true);
        })
        .catch(() => { if (alive) { setResults([]); setOpen(false); } });
    }, 120);
    return () => { alive = false; clearTimeout(t); };
  }, [value, fetch]);

  const pick = (item: T) => {
    typed.current = false;
    setOpen(false);
    onPick(item);
  };

  const hintNode = open && hint ? hint(value.trim(), results) : null;

  return (
    <div className="relative">
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => { typed.current = true; onChange(e.target.value); }}
        onKeyDown={(e) => {
          if (open && results.length > 0) {
            if (e.key === 'ArrowDown') { e.preventDefault(); setHl((i) => Math.min(results.length - 1, i + 1)); return; }
            if (e.key === 'ArrowUp') { e.preventDefault(); setHl((i) => Math.max(0, i - 1)); return; }
            if (e.key === 'Enter' && hl < 0) setOpen(false);
            if (e.key === 'Enter' && hl >= 0) { e.preventDefault(); pick(results[hl]); return; }
          }
          if (e.key === 'Escape' && open) { e.preventDefault(); setOpen(false); return; }
          onKeyDown?.(e);
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-autocomplete="list"
        aria-expanded={open}
        inputMode={inputMode}
        autoFocus={autoFocus}
        autoComplete="off"
        spellCheck={false}
        data-testid={testId}
        className={className}
      />
      {open && (results.length > 0 || hintNode) && (
        <ul
          role="listbox"
          style={pos}
          className={cn(
            'z-50 min-w-[18rem] overflow-auto rounded-md border border-stroke-hairline/15 bg-bg-surface py-1 shadow-lg',
            listClassName,
          )}
        >
          {results.map((item, i) => {
            const v = view(item);
            return (
              <li key={i} role="option" aria-selected={i === hl}>
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); pick(item); }}
                  onMouseEnter={() => setHl(i)}
                  className={cn(
                    'flex w-full items-start justify-between gap-3 px-3 py-2 text-left',
                    i === hl ? 'bg-accent/10' : 'hover:bg-bg-subtle',
                  )}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-body text-fg-primary">{v.primary}</span>
                    {v.secondary && <span className="block truncate text-label text-fg-tertiary">{v.secondary}</span>}
                  </span>
                  {v.tag && <span className="shrink-0 rounded bg-bg-subtle px-1.5 py-0.5 text-label text-fg-secondary">{v.tag}</span>}
                </button>
              </li>
            );
          })}
          {hintNode && <li className="border-t border-stroke-hairline/10 px-3 py-2 text-label text-accent-ink">{hintNode}</li>}
        </ul>
      )}
    </div>
  );
}
